/** Shared by Automation > Automation logs and the Sentinel Workspace's Review queue. */

import { addDays, startOfDay } from 'date-fns';

export const PROVIDERS: { id: string; label: string; dot: string }[] = [
	{ id: 'gorgias', label: 'Gorgias', dot: 'bg-orange-400' },
	{ id: 'zendesk', label: 'Zendesk', dot: 'bg-emerald-500' },
	{ id: 'zoho', label: 'Zoho', dot: 'bg-red-400' },
	{ id: 'richpanel', label: 'Richpanel', dot: 'bg-sky-500' },
	{ id: 'kustomer', label: 'Kustomer', dot: 'bg-violet-500' },
	{ id: 'gladly', label: 'Gladly', dot: 'bg-amber-500' },
	{ id: 'freshdesk', label: 'Freshdesk', dot: 'bg-green-500' },
	{ id: 'helpscout', label: 'Help Scout', dot: 'bg-blue-500' },
	{ id: 'intercom', label: 'Intercom', dot: 'bg-slate-500' },
	{ id: 'helpdesk', label: 'Kim Helpdesk', dot: 'bg-indigo-500' }
];
export const providerOf = (id: string) => PROVIDERS.find((p) => p.id === id) ?? { id, label: id, dot: 'bg-gray-500' };

export const humanize = (intent: string) => intent.replace(/_/g, ' ');

/**
 * Why Kim held a reply back, in words. Brahma's codes come from its response decider
 * (`v3_automation_block_reason`); others are from the backend's shouldAutomateDraftReply guards or reply handling.
 */
const BLOCK_REASONS: Record<string, string> = {
	human_touched: 'An agent has already replied on this ticket',
	prior_bot: 'Kim already used the replies this channel allows on one ticket',
	novel_intent: 'An intent Kim is not yet cleared to answer alone',
	multi_intent: 'The customer asked about more than one thing',
	critical_tool_error: 'A lookup the reply needed failed',
	not_filled: 'Kim could not complete the reply',
	quality_below_threshold: 'The quality check scored it below the auto-send threshold',
	invalid_quality_score: 'The quality check gave no usable score',
	routing_not_auto_reply: 'Kim routed the ticket to an agent',
	opus_judge_error: 'The quality judge could not run',
	opus_judge_failed: 'The quality judge rejected the reply',
	unknown_role: 'Kim could not tell who wrote the last message',
	declined: 'Brahma did not send it and gave no reason',
	conversation_status_resolved: 'The ticket was already closed',
	conversation_not_found: 'Kim could not find the ticket',
	conversation_lookup_failed: 'The ticket could not be read',
	human_reply_exists: 'An agent has already replied on this ticket',
	outside_operating_hours: "Outside the brand's automation hours",
	below_confidence_threshold: "Kim's confidence was below the account's threshold",
	send_failed: 'The helpdesk did not accept the reply'
};
export const blockReasonLabel = (reason: string) => BLOCK_REASONS[reason] ?? humanize(reason);
export const blockedByLabel = (by: 'brahma' | 'kim') => (by === 'brahma' ? 'Brahma' : 'Automation guard');

export type DayRange = { startDate?: Date; endDate?: Date };

/** Whole local days as the API's sent-time bounds: a single day is a range that starts and ends on it. */
export const sentBetween = ({ startDate, endDate }: DayRange): { from?: string; to?: string } => ({
	from: startDate && startOfDay(startDate).toISOString(),
	to: endDate && addDays(startOfDay(endDate), 1).toISOString()
});
